import { useState } from "react";
import { sendPartnershipRequest } from "../../utils/sendPartnershipRequest";
import { toast } from "react-toastify";

import { brandingConfig } from "../../config/branding";
import { useT } from "../../stores/uiLang";

const CONTACT_T = {
  en: {
    fillAll: "Please fill in all fields.",
    sent: "Partnership request sent successfully!",
    failed: "Failed to send partnership request.",
    h2: "Ready to bring bilingual literacy to your classrooms?",
    p: "Join schools and districts bringing bilingual, story-driven literacy into their K-5 classrooms.",
    items: [
      "Standards-aligned K-5 curriculum",
      "English + Spanish, composed natively",
      "Four reading tiers per chapter",
      "Flexible, budget-friendly implementation",
    ],
    inquiries: "For partnership inquiries:",
    formH: "Request a Partnership Demo",
    first: "First name",
    last: "Last name",
    inst: "Institution",
    email: "Email",
    sending: "Sending...",
    book: "Book a Demo",
  },
  es: {
    fillAll: "Complete todos los campos.",
    sent: "¡Solicitud de alianza enviada con éxito!",
    failed: "No se pudo enviar la solicitud de alianza.",
    h2: "¿Listos para llevar la lectoescritura bilingüe a sus salones?",
    p: "Únase a las escuelas y los distritos que llevan la lectoescritura bilingüe basada en cuentos a sus salones de K a 5.º grado.",
    items: [
      "Currículo de K a 5.º grado alineado con los estándares",
      "Inglés + español, escritos de forma nativa",
      "Cuatro niveles de lectura por capítulo",
      "Implementación flexible y económica",
    ],
    inquiries: "Para consultas sobre alianzas:",
    formH: "Solicitar una demostración de alianza",
    first: "Nombre",
    last: "Apellido",
    inst: "Institución",
    email: "Correo electrónico",
    sending: "Enviando...",
    book: "Reservar una demostración",
  },
};

export function ContactForm() {
  const [loading, setLoading] = useState(false);
  const t = useT(CONTACT_T);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const firstName = formData.get("first_name")?.toString().trim() || "";
    const lastName = formData.get("last_name")?.toString().trim() || "";
    const institution = formData.get("institution")?.toString().trim() || "";
    const email = formData.get("email")?.toString().trim() || "";

    if (!firstName || !lastName || !institution || !email) {
      toast.error(t.fillAll);
      return;
    }

    setLoading(true);

    const result = await sendPartnershipRequest({
      firstName,
      lastName,
      institution,
      email,
    });

    setLoading(false);
    if (result.success) toast.success(t.sent);
    else toast.error(t.failed);

    if (result.success) form.reset();
  };

  return (
    <div>
      <div className="mx-auto max-w-6xl px-6">
        <div className="bg-white border-2 border-[#3b5839]/20 rounded-xl shadow-2xl overflow-hidden transition-all duration-300  hover:-translate-y-1">
          <div className="px-6 py-12 sm:px-12 lg:px-16 lg:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Info Section */}
            <div>
              <h2 className="text-3xl text-gray-900 font-semibold">
                {t.h2}
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                {t.p}
              </p>
              <ul className="mt-8 space-y-4">
                {t.items.map((item) => (
                  <li key={item} className="flex items-start">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-gray-600 mt-1"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    <span className="ml-3 text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <p className="text-gray-600">
                  {t.inquiries}
                </p>
                <a
                  href="mailto:hello@startupsmartup.com"
                  className="text-gray-600 font-semibold hover:text-[#3b5839]"
                >
                  hello@startupsmartup.com
                </a>
              </div>
            </div>

            {/* Form Section */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-xl font-medium text-gray-900 mb-6">
                {t.formH}
              </h3>
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="first_name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t.first}
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      id="first_name"
                      required
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="last_name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t.last}
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      id="last_name"
                      required
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="institution"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.inst}
                  </label>
                  <input
                    type="text"
                    name="institution"
                    id="institution"
                    required
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.email}
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn"
                  >
                    {loading ? t.sending : t.book}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
